// View profiles (doc 03 and doc 04, prototype "Podgląd jako"): which screens the navigation offers, where the user
// starts and how dense the layout is. A profile is not a permission: hidden screens stay reachable by address and
// the host checks every request. It only fits the application to the job at hand.

export type ProfileId = "manager" | "installer" | "owner" | "cluster";
export type Density = "compact" | "comfortable";

export interface NavSection {
  /** Translation key under `nav.`. */
  key: string;
  paths: string[];
}

export interface ViewProfile {
  id: ProfileId;
  sections: NavSection[];
  start: string;
  /** Phones: up to four screens in the bottom bar, the rest under "More". */
  bottom: string[];
  density: Density;
  /** Offered only to people who may change configuration (admin, operator). */
  needsOperate: boolean;
}

export const PROFILES: readonly ViewProfile[] = [
  {
    id: "manager",
    sections: [
      {
        key: "monitoring",
        paths: [
          "/overview",
          "/analysis",
          "/flows",
          "/events",
          "/automations",
          "/charging",
          "/plans",
          "/reports",
          "/finance",
          "/optimization",
          "/cluster",
          "/assets",
          "/devices",
        ],
      },
      {
        key: "configuration",
        paths: [
          "/commissioning",
          "/integrations",
          "/users",
          "/extensions",
          "/catalog",
        ],
      },
    ],
    start: "/overview",
    bottom: ["/overview", "/assets", "/devices", "/integrations"],
    density: "compact",
    needsOperate: false,
  },
  {
    // Commissioning and diagnostics: places, connections and devices first; no energy figures up front.
    id: "installer",
    sections: [
      {
        key: "commissioning",
        paths: [
          "/commissioning",
          "/assets",
          "/plans",
          "/events",
          "/automations",
          "/integrations",
          "/devices",
        ],
      },
      { key: "configuration", paths: ["/extensions", "/catalog"] },
    ],
    start: "/assets",
    bottom: ["/assets", "/integrations", "/devices"],
    density: "compact",
    needsOperate: true,
  },
  {
    // A household: what is happening at home and the devices there, in larger type.
    id: "owner",
    sections: [
      {
        key: "myPlaces",
        paths: [
          "/places",
          "/overview",
          "/analysis",
          "/automations",
          "/charging",
          "/events",
          "/devices",
        ],
      },
    ],
    start: "/places",
    bottom: ["/places", "/overview", "/events", "/devices"],
    density: "comfortable",
    needsOperate: false,
  },
  {
    // An energy cluster's operator: the cluster's balance, members and sharing first.
    id: "cluster",
    sections: [
      { key: "clusterOps", paths: ["/cluster", "/overview", "/reports"] },
    ],
    start: "/cluster",
    bottom: ["/cluster", "/overview", "/reports"],
    density: "compact",
    needsOperate: false,
  },
];

export function profilesFor(canOperate: boolean): ViewProfile[] {
  return PROFILES.filter((p) => canOperate || !p.needsOperate);
}

/** The remembered profile if it is still offered to the user, else the first one offered. */
export function resolveProfile(
  stored: string | null,
  canOperate: boolean,
): ViewProfile {
  const offered = profilesFor(canOperate);
  return offered.find((p) => p.id === stored) ?? offered[0]!;
}
